import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, LayoutGroup } from "framer-motion";
import { useCatalog, findBottle, bundleSaving, pricePerLitre } from "@/lib/shop";
import OilFill from "@/components/motion/OilFill";
import SlidingNumber from "@/components/motion/SlidingNumber";
import AddToBasket from "./AddToBasket";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const USES = [
  { key: "daily", label: "Cooking every day" },
  { key: "sometimes", label: "Cooking now and then" },
  { key: "care", label: "Hair and skin" },
  { key: "try", label: "Just trying it" },
];
const HOMES = [
  { key: "small", label: "1 or 2" },
  { key: "mid", label: "3 or 4" },
  { key: "big", label: "5 or more" },
];

/** Our rule of thumb, stated as a suggestion on the page. */
function suggest(use, home) {
  if (use === "care") return { bestFor: "Chintu Edition (100 ml)", fill: 0.16, why: "A small jar sits on the dressing table and lasts through many weekly champis." };
  if (use === "try") return { bestFor: "250 ml", fill: 0.3, why: "Enough to cook with for a couple of weeks and decide if you like the taste." };
  if (use === "sometimes") return home === "big"
    ? { bestFor: "1 L", fill: 0.85, why: "A bigger home goes through oil faster, and the litre costs less per ml." }
    : { bestFor: "500 ml", fill: 0.55, why: "Used a few times a week, a 500 ml jar stays fresh and gets finished." };
  if (home === "small") return { bestFor: "1 L", fill: 0.85, why: "Daily cooking for one or two usually finishes a litre comfortably." };
  if (home === "mid") return { bestFor: "Family Pack", fill: 1, why: "Two litres in one delivery, priced below two single jars." };
  return { bestFor: "Family Pack 3", fill: 1, why: "Three litres in one delivery for a big kitchen, priced below three single jars." };
}

function Chips({ options, value, onChange, group }) {
  return (
    <LayoutGroup id={group}>
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {options.map((o) => {
          const on = value === o.key;
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(o.key)}
              className={cn("relative rounded-control px-4 py-2 font-body text-[14px] transition-colors", on ? "text-white" : "text-brand-bone/80 hover:text-white bg-white/5")}
              data-testid={`finder-${group}-${o.key}`}
            >
              {on && <motion.span layoutId={`${group}-pill`} className="absolute inset-0 rounded-control bg-brand-gold" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className="relative">{o.label}</span>
            </button>
          );
        })}
      </div>
    </LayoutGroup>
  );
}

export default function SizeFinder({ title = "Which jar is right for you?" }) {
  const products = useCatalog();
  const [use, setUse] = useState("daily");
  const [home, setHome] = useState("mid");
  const s = suggest(use, home);
  let match = findBottle(products, s.bestFor.replace(" 3", ""));
  if (s.bestFor === "Family Pack 3" && match?.product) {
    match = { product: match.product, variant: match.product.variants?.find((v) => /^3/.test(v.size)) || match.variant };
  }
  const saving = match?.variant ? bundleSaving(match.variant, products) : null;
  const ppl = match?.variant ? pricePerLitre(match.variant) : null;
  const pick = (setter, kind) => (v) => { setter(v); track("size_finder", { step: kind, value: v }); };

  return (
    <section className="bg-brand-obsidian text-brand-bone py-20 sm:py-28" data-testid="size-finder">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <div>
          <p className="font-body text-[15px] font-semibold text-brand-bone/70">Jar finder</p>
          <h2 className="mt-2 font-display text-white text-[clamp(2rem,5vw,3.25rem)] leading-[1.04] tracking-display">{title}</h2>
          <p className="mt-4 max-w-xl font-body text-[17px] leading-relaxed text-brand-bone/70">Two taps and we will suggest a size. It is our rule of thumb from how Indian homes use coconut oil, so pick whatever feels right.</p>

          <p className="mt-8 font-body text-[14px] font-semibold text-white">What is it mostly for?</p>
          <div className="mt-3"><Chips options={USES} value={use} onChange={pick(setUse, "use")} group="use" /></div>

          {(use === "daily" || use === "sometimes") && (
            <>
              <p className="mt-6 font-body text-[14px] font-semibold text-white">How many people eat at home?</p>
              <div className="mt-3"><Chips options={HOMES} value={home} onChange={pick(setHome, "home")} group="home" /></div>
            </>
          )}
        </div>

        <div className="rounded-tile bg-white/5 border border-white/10 p-6 sm:p-8 flex items-center gap-4 sm:gap-6" aria-live="polite">
          <OilFill level={s.fill} label={`Suggested: ${s.bestFor}`} className="h-28 sm:h-44 w-auto shrink-0" />
          <div className="min-w-0">
            <p className="font-body text-[13px] font-semibold text-brand-gold">Our suggestion</p>
            {match?.variant ? (
              <>
                <Link to={`/products/${match.product.slug}`} className="mt-1 block font-display text-white text-[24px] leading-tight hover:underline">
                  {match.product.name.replace("Karijeeva ", "")}, {match.variant.size}
                </Link>
                <p className="mt-2 font-display text-white text-[30px] tracking-[-0.02em]"><SlidingNumber value={match.variant.price} prefix="₹" /></p>
                <p className="font-body text-[13px] text-brand-bone/60">
                  {ppl ? `₹${ppl} a litre. ` : ""}Inclusive of taxes.
                  {saving ? ` You save ₹${saving.saving} against ${match.variant.size.split("×")[0].trim()} single jars.` : ""}
                </p>
                <p className="mt-3 font-body text-[14px] leading-snug text-brand-bone/75">{s.why}</p>
                <AddToBasket product={match.product} variant={match.variant} className="mt-5 whitespace-nowrap px-5" testid="finder-add" />
              </>
            ) : (
              <p className="mt-2 font-body text-brand-bone/70">Loading jars</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
